import type { Block, SurfaceSpec } from "./surface-spec";
import { validateSurfaceSpec } from "./validate";
import type { Catalog, ValidationResult } from "./validate";
import { isRegisteredBlockType } from "./block-registry";
import { ecosystemSectionId } from "./block-contract";

/**
 * renderSurfaceSpec — turns a validated SurfaceSpec into DOM for the canvas overlay.
 *
 * Architecture (modular + composable + extensible):
 *   - Each block type is rendered by a small BlockRenderer registered in
 *     `blockRenderers`. A renderer fills the block's host element from shared
 *     primitives (`el`, `media`, `list`). Adding/overriding a type is one call to
 *     `registerBlockRenderer` — no growing switch; imagery/effects live in one place.
 *
 * Two guarantees stay structural, not conventional:
 *   - The render is GATED by the validator. An invalid spec returns { root: null } —
 *     LLM output cannot reach the DOM unvalidated.
 *   - All copy is written via textContent, never innerHTML. Image URLs are sanitised
 *     to http(s)/data:image before they touch an <img src>.
 *
 * Style is a reference in the spec; the resolved StylePack is applied as --gem-*
 * custom properties so the same spec renders on-brand per store.
 */

export interface StylePack {
  ref: string;
  /** Token map, e.g. { "--gem-color-primary": "#F4552E", "--gem-radius-md": "12px" }. */
  tokens: Record<string, string>;
}

export interface RenderResult {
  root: HTMLElement | null;
  validation: ValidationResult;
}

/** Passed to every renderer so blocks can compose position-aware behaviour (effects). */
export interface RenderContext {
  index: number;
  total: number;
}

/** A renderer fills `host` (the block element) — it may append children and set attrs. */
export type BlockRenderer = (block: Block, host: HTMLElement, ctx: RenderContext) => void;

export function renderSurfaceSpec(spec: SurfaceSpec, stylePack: StylePack, catalog?: Catalog): RenderResult {
  const validation = validateSurfaceSpec(spec, catalog);
  if (!validation.ok) return { root: null, validation };

  const root = document.createElement("div");
  root.className = "gemsales-surface";
  root.setAttribute("data-surface-id", spec.surfaceId);
  root.setAttribute("data-depth", String(spec.intent.depth));
  root.setAttribute("data-intent", spec.intent.cluster);
  root.setAttribute("data-style-pack", stylePack.ref);

  for (const [name, value] of Object.entries(stylePack.tokens)) {
    if (name.startsWith("--")) root.style.setProperty(name, value);
  }

  const total = spec.blocks.length;
  spec.blocks.forEach((block, index) => {
    root.appendChild(renderBlock(block, { index, total }));
  });
  return { root, validation };
}

/** Registry of per-type renderers. Extend/override via registerBlockRenderer. */
const blockRenderers: Record<string, BlockRenderer> = {};

export function registerBlockRenderer(type: string, renderer: BlockRenderer): void {
  blockRenderers[type] = renderer;
}

function renderBlock(block: Block, ctx: RenderContext): HTMLElement {
  const sec = el("div", `gemsales-block gemsales-block--${block.type}`);
  sec.setAttribute("data-block-id", block.id);
  sec.setAttribute("data-origin", block.origin);
  // Seamless handshake with the gemtheme kernel: every rendered atom carries the
  // ecosystem section id it pins to (hook→gemsales-hook, bundle→gem-bundle). The
  // generated surface speaks the store's section vocabulary in the DOM — the seam
  // that lets analytics, reconciliation, and render-parity build on one contract.
  sec.setAttribute("data-gem-atom", block.type);
  if (isRegisteredBlockType(block.type)) sec.setAttribute("data-gem-section", ecosystemSectionId(block.type));
  // Staggered entrance effect — each block carries its ordinal; CSS turns it into a
  // delay. Purely presentational, disabled under prefers-reduced-motion.
  sec.classList.add("gemsales-reveal");
  sec.style.setProperty("--gem-reveal-i", String(ctx.index));
  (blockRenderers[block.type] ?? renderDefault)(block, sec, ctx);
  return sec;
}

// ── Composable primitives ────────────────────────────────────────────────────

function el(tag: string, className: string, text?: string): HTMLElement {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/**
 * Media slot — renders a real <img> when a safe URL is present, else a themed
 * placeholder box. One primitive, reused by hero/product/bundle so imagery is
 * consistent and added in a single place.
 */
function media(src: string | undefined, alt: string, className: string): HTMLElement {
  const box = el("div", `${className} gemsales-media`);
  const safe = safeUrl(src);
  if (safe) {
    const img = document.createElement("img");
    img.className = "gemsales-media__img";
    img.src = safe;
    img.alt = alt || "";
    img.loading = "lazy";
    img.decoding = "async";
    box.appendChild(img);
    box.setAttribute("data-has-image", "true");
  } else {
    box.setAttribute("data-placeholder", "true");
  }
  return box;
}

/** Generic list builder: maps records → row nodes into a container. */
function list(
  raws: unknown,
  containerClass: string,
  build: (row: Record<string, unknown>) => HTMLElement | null,
): HTMLElement {
  const container = el("div", containerClass);
  for (const raw of asArray(raws)) {
    const row = asRecord(raw);
    if (!row) continue;
    const node = build(row);
    if (node) container.appendChild(node);
  }
  return container;
}

// ── Block renderers ──────────────────────────────────────────────────────────

registerBlockRenderer("answer", renderProse);
registerBlockRenderer("agitate", renderProse);
registerBlockRenderer("summary", renderProse);
function renderProse(block: Block, host: HTMLElement): void {
  const copy = copyOf(block);
  if (copy.title) host.appendChild(el("h4", "gemsales-block__title", copy.title));
  host.appendChild(el("p", "gemsales-block__body", copy.body ?? copy.text ?? ""));
}

registerBlockRenderer("hook", (block, host) => {
  const copy = copyOf(block);
  const img = imageOf(block);
  if (img) host.appendChild(media(img, copy.headline ?? "", "gemsales-hero__media"));
  host.appendChild(el("h3", "gemsales-block__headline", copy.headline ?? copy.title ?? ""));
  const sub = copy.sub ?? copy.subcopy;
  if (sub) host.appendChild(el("p", "gemsales-block__sub", sub));
});

registerBlockRenderer("proof", (block, host) => {
  const copy = copyOf(block);
  host.setAttribute("data-proof-ref", block.binding?.proofRef ?? "");
  const img = imageOf(block);
  if (img) host.appendChild(media(img, copy.label ?? "", "gemsales-proof__media"));
  host.appendChild(el("p", "gemsales-block__proof-label", copy.label ?? copy.title ?? ""));
  if (copy.body) host.appendChild(el("p", "gemsales-block__body", copy.body));
});

registerBlockRenderer("metric", (block, host) => {
  const copy = copyOf(block);
  host.appendChild(el("span", "gemsales-metric__value", copy.value ?? copy.label ?? ""));
  if (copy.detail) host.appendChild(el("span", "gemsales-metric__detail", copy.detail));
});

registerBlockRenderer("guarantee", renderBadge);
registerBlockRenderer("urgency", renderBadge);
function renderBadge(block: Block, host: HTMLElement): void {
  const copy = copyOf(block);
  host.appendChild(el("span", "gemsales-badge", copy.label ?? copy.body ?? ""));
}

registerBlockRenderer("hero", (block, host) => {
  const copy = copyOf(block);
  host.appendChild(media(imageOf(block), copy.headline ?? "", "gemsales-hero__media"));
  host.appendChild(el("h3", "gemsales-block__headline", copy.headline ?? copy.title ?? ""));
  const sub = copy.sub ?? copy.subcopy ?? copy.body;
  if (sub) host.appendChild(el("p", "gemsales-block__sub", sub));
});

registerBlockRenderer("product", (block, host) => {
  const copy = copyOf(block);
  host.appendChild(media(imageOf(block), copy.title ?? copy.name ?? "", "gemsales-product__media"));
  const info = el("div", "gemsales-product__info");
  const name = copy.title ?? copy.name;
  if (name) info.appendChild(el("h4", "gemsales-block__title", name));
  const body = copy.body ?? copy.description;
  if (body) info.appendChild(el("p", "gemsales-block__body", body));
  if (str(block.slots.priceLabel)) info.appendChild(el("span", "gemsales-product__price", str(block.slots.priceLabel)));
  host.appendChild(info);
});

registerBlockRenderer("bundle", (block, host) => {
  const copy = copyOf(block);
  if (copy.title) host.appendChild(el("h4", "gemsales-block__title", copy.title));
  host.appendChild(
    list(block.slots.options, "gemsales-bundle__options", (option) => {
      const card = el("div", "gemsales-bundle__option");
      if (option.featured === true) card.setAttribute("data-featured", "true");
      if (option.productRef) card.setAttribute("data-product-ref", str(option.productRef));
      const thumb = safeUrl(str(option.image));
      if (thumb) card.appendChild(media(thumb, str(option.label), "gemsales-bundle__thumb"));
      card.appendChild(el("span", "gemsales-bundle__label", str(option.label)));
      if (typeof option.qty === "number") card.appendChild(el("span", "gemsales-bundle__qty", `×${option.qty}`));
      if (str(option.priceLabel)) card.appendChild(el("span", "gemsales-bundle__price", str(option.priceLabel)));
      return card;
    }),
  );
  if (str(block.slots.saveLabel)) host.appendChild(el("span", "gemsales-bundle__save", str(block.slots.saveLabel)));
});

registerBlockRenderer("comparison", (block, host) => {
  const copy = copyOf(block);
  if (copy.title) host.appendChild(el("h4", "gemsales-block__title", copy.title));
  host.appendChild(
    list(block.slots.rows, "gemsales-comparison", (row) => {
      const line = el("div", "gemsales-comparison__row");
      line.appendChild(el("span", "gemsales-comparison__label", str(row.label)));
      line.appendChild(el("span", "gemsales-comparison__value", str(row.value)));
      return line;
    }),
  );
});

registerBlockRenderer("faq", (block, host) => {
  const copy = copyOf(block);
  if (copy.title) host.appendChild(el("h4", "gemsales-block__title", copy.title));
  host.appendChild(
    list(block.slots.items, "gemsales-faq", (item) => {
      const entry = el("div", "gemsales-faq__item");
      entry.appendChild(el("p", "gemsales-faq__q", str(item.q ?? item.question)));
      entry.appendChild(el("p", "gemsales-faq__a", str(item.a ?? item.answer)));
      return entry;
    }),
  );
});

registerBlockRenderer("reviews", (block, host) => {
  const copy = copyOf(block);
  if (copy.title) host.appendChild(el("h4", "gemsales-block__title", copy.title));
  host.appendChild(
    list(block.slots.items ?? block.slots.reviews, "gemsales-reviews", (review) => {
      const card = el("div", "gemsales-review");
      const rating = typeof review.rating === "number" ? Math.max(1, Math.min(5, Math.round(review.rating))) : 5;
      card.appendChild(el("span", "gemsales-review__stars", "★★★★★".slice(0, rating)));
      card.appendChild(el("p", "gemsales-review__text", str(review.text ?? review.quote ?? review.body)));
      const author = review.author ?? review.name;
      if (author) card.appendChild(el("span", "gemsales-review__author", str(author)));
      return card;
    }),
  );
});

registerBlockRenderer("timeline", (block, host) => {
  const copy = copyOf(block);
  if (copy.title) host.appendChild(el("h4", "gemsales-block__title", copy.title));
  host.appendChild(
    list(block.slots.items ?? block.slots.rows ?? block.slots.steps, "gemsales-timeline", (step) => {
      const row = el("div", "gemsales-timeline__step");
      row.appendChild(el("span", "gemsales-timeline__label", str(step.label ?? step.time ?? step.title)));
      const text = step.text ?? step.body ?? step.value ?? step.detail;
      if (text) row.appendChild(el("p", "gemsales-timeline__text", str(text)));
      return row;
    }),
  );
});

registerBlockRenderer("cta", (block, host) => {
  const copy = copyOf(block);
  const btn = el("button", "gemsales-cta", copy.label ?? "");
  btn.setAttribute("type", "button");
  const action = str(block.slots.action);
  if (action) btn.setAttribute("data-action", action);
  const target = asRecord(block.slots.target);
  if (target) {
    if (target.bundleRef) btn.setAttribute("data-target-bundle", str(target.bundleRef));
    if (target.productRef) btn.setAttribute("data-target-product", str(target.productRef));
  }
  host.appendChild(btn);
});

/** Fallback for any unregistered/future type — safe display fields only. */
function renderDefault(block: Block, host: HTMLElement): void {
  const copy = copyOf(block);
  const img = imageOf(block);
  if (img) host.appendChild(media(img, copy.title ?? "", "gemsales-hero__media"));
  const heading = copy.title ?? copy.headline ?? copy.name;
  if (heading) host.appendChild(el("h4", "gemsales-block__title", heading));
  const body = copy.body ?? copy.text ?? copy.description ?? copy.sub;
  if (body) host.appendChild(el("p", "gemsales-block__body", body));
  if (str(block.slots.priceLabel)) host.appendChild(el("span", "gemsales-price", str(block.slots.priceLabel)));
}

// ── Data helpers ─────────────────────────────────────────────────────────────

function copyOf(block: Block): Record<string, string> {
  const copy = block.slots.copy;
  return copy && typeof copy === "object" ? (copy as Record<string, string>) : {};
}

/** Resolve a block's illustrative image from the usual slot locations. */
function imageOf(block: Block): string | undefined {
  const copy = copyOf(block);
  const candidates = [copy.image, copy.media, str(block.slots.image), str(block.slots.media)];
  for (const c of candidates) {
    const safe = safeUrl(typeof c === "string" ? c : undefined);
    if (safe) return safe;
  }
  return undefined;
}

/** Only http(s) or data:image URLs may reach an <img src>. Everything else drops. */
function safeUrl(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const v = value.trim();
  if (/^https?:\/\//i.test(v) || /^data:image\//i.test(v)) return v;
  return undefined;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function str(value: unknown): string {
  return typeof value === "string" ? value : "";
}
